import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { LanguageProvider } from "./lib/i18n";
import { Toaster } from "./components/ui/sonner";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import BrokerLanding from "./pages/BrokerLanding";
import BorrowerIntake from "./pages/BorrowerIntake";
import NotFound from "./pages/NotFound";

import SponsorLayout from "./pages/sponsor/SponsorLayout";
import SponsorOverview from "./pages/sponsor/SponsorOverview";
import SponsorApplications from "./pages/sponsor/SponsorApplications";
import SponsorApplicationDetail from "./pages/sponsor/SponsorApplicationDetail";
import SponsorSubmit from "./pages/sponsor/SponsorSubmit";
import SponsorReferral from "./pages/sponsor/SponsorReferral";
import SponsorLeads from "./pages/sponsor/SponsorLeads";
import SponsorMarketingKit from "./pages/sponsor/SponsorMarketingKit";
import SponsorTraining from "./pages/sponsor/SponsorTraining";
import SponsorAccount from "./pages/sponsor/SponsorAccount";
import SponsorMsa from "./pages/sponsor/SponsorMsa";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminApplications from "./pages/admin/AdminApplications";
import AdminApplicationDetail from "./pages/admin/AdminApplicationDetail";
import AdminBrokers from "./pages/admin/AdminBrokers";
import AdminBorrowers from "./pages/admin/AdminBorrowers";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import AdminMarketingTemplates from "./pages/admin/AdminMarketingTemplates";

import BorrowerDashboard from "./pages/borrower/BorrowerDashboard";
import BorrowerInvite from "./pages/BorrowerInvite";

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-400">
      <div className="flex items-center gap-3 text-sm">
        <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
        Loading…
      </div>
    </div>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, checking } = useAuth();
  if (checking) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={redirectForRole(user.role)} replace />;
  }
  // Gate sponsors behind MSA signing
  if (
    user.role === "sponsor" &&
    !user.msa_signed &&
    typeof window !== "undefined" &&
    !window.location.pathname.startsWith("/broker/msa")
  ) {
    return <Navigate to="/broker/msa" replace />;
  }
  return children;
}

function redirectForRole(role) {
  if (role === "admin") return "/admin";
  if (role === "sponsor") return "/broker";
  if (role === "borrower") return "/borrower";
  return "/login";
}

function HomeRedirect() {
  const { user, checking } = useAuth();
  if (checking) return <LoadingScreen />;
  if (user) return <Navigate to={redirectForRole(user.role)} replace />;
  return <Landing />;
}

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/s/:slug" element={<BrokerLanding />} />
          <Route path="/apply" element={<BorrowerIntake />} />

          <Route
            path="/broker/msa"
            element={
              <ProtectedRoute roles={["sponsor"]}>
                <SponsorMsa />
              </ProtectedRoute>
            }
          />

          <Route
            path="/broker"
            element={
              <ProtectedRoute roles={["sponsor"]}>
                <SponsorLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<SponsorOverview />} />
            <Route path="applications" element={<SponsorApplications />} />
            <Route path="applications/:id" element={<SponsorApplicationDetail />} />
            <Route path="submit" element={<SponsorSubmit />} />
            <Route path="referral" element={<SponsorReferral />} />
            <Route path="leads" element={<SponsorLeads />} />
            <Route path="marketing" element={<SponsorMarketingKit />} />
            <Route path="training" element={<SponsorTraining />} />
            <Route path="account" element={<SponsorAccount />} />
          </Route>

          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="applications" element={<AdminApplications />} />
            <Route path="applications/:id" element={<AdminApplicationDetail />} />
            <Route path="submit" element={<SponsorSubmit />} />
            <Route path="brokers" element={<AdminBrokers />} />
            <Route path="borrowers" element={<AdminBorrowers />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="audit-log" element={<AdminAuditLog />} />
            <Route path="marketing-templates" element={<AdminMarketingTemplates />} />
          </Route>

          <Route path="/borrower/invite" element={<BorrowerInvite />} />
          <Route
            path="/borrower"
            element={
              <ProtectedRoute roles={["borrower"]}>
                <BorrowerDashboard />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster
          position="bottom-right"
          theme="dark"
          toastOptions={{
            style: {
              background: "rgb(15 23 42)",
              border: "1px solid rgb(30 41 59)",
              color: "rgb(226 232 240)",
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
    </LanguageProvider>
  );
}
